import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  Avatar,
  Menu,
  MenuItem,
  FormControl,
  Select,
  InputLabel,
  OutlinedInput,
  TextField,
  InputAdornment,
  LinearProgress
} from '@mui/material';

import {
  MoreVert as MoreIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  CheckCircle as ResolvedIcon,
  Error as UrgentIcon,
  Pending as PendingIcon,
  Help as OpenIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';

import { TICKET_CATEGORIES, TICKET_STATUSES, TICKET_PRIORITIES } from '../utils/constants';
import { formatTimestamp } from '../utils/helpers';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const DashboardTab = ({ tickets = [], onSelectTicket }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuTicketId, setMenuTicketId] = useState(null);

  // Computed statistics
  const stats = {
    total: tickets.length,
    open: tickets.filter(t => t.status === 'open').length,
    pending: tickets.filter(t => t.status === 'pending').length,
    urgent: tickets.filter(t => t.status === 'urgent').length,
    closed: tickets.filter(t => t.status === 'closed').length,
    unread: tickets.filter(t => t.unread).length,
    unassigned: tickets.filter(t => !t.assignedTo).length
  };

  // Data for category pie chart
  const categoryData = TICKET_CATEGORIES.map(category => ({
    name: category.label,
    value: tickets.filter(t => t.category === category.id).length,
    color: category.color
  })).filter(item => item.value > 0);

  // Data for status bar chart
  const statusData = Object.entries(TICKET_STATUSES).map(([key, { label, color }]) => ({
    name: label,
    value: tickets.filter(t => t.status === key).length,
    color: color
  }));

  // Chart colors
  const CHART_COLORS = ['#4A90E2', '#50C878', '#FF6B6B', '#FFD700', '#9370DB'];

  // Handle menu open
  const handleMenuOpen = (event, ticketId) => {
    setAnchorEl(event.currentTarget);
    setMenuTicketId(ticketId);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuTicketId(null);
  };

  // Handle ticket selection
  const handleRowClick = (ticketId) => {
    onSelectTicket(ticketId);
  };

  // Handle search
  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  // Handle status filter
  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  // Handle category filter
  const handleCategoryFilterChange = (event) => {
    setCategoryFilter(event.target.value);
  };

  // Filter tickets based on search and filters
  useEffect(() => {
    let result = [...tickets];

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(ticket => 
        ticket.id.toLowerCase().includes(query) || 
        ticket.subject.toLowerCase().includes(query) || 
        ticket.customer.name.toLowerCase().includes(query) ||
        ticket.customer.email?.toLowerCase().includes(query)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      result = result.filter(ticket => ticket.status === statusFilter);
    }

    // Filter by category
    if (categoryFilter !== 'all') {
      result = result.filter(ticket => ticket.category === categoryFilter);
    }

    setFilteredTickets(result);
  }, [tickets, searchQuery, statusFilter, categoryFilter]);

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Status icon component
  const StatusIcon = ({ status }) => {
    switch(status) {
      case 'open':
        return <OpenIcon fontSize="small" sx={{ color: TICKET_STATUSES.open.color }} />;
      case 'pending':
        return <PendingIcon fontSize="small" sx={{ color: TICKET_STATUSES.pending.color }} />;
      case 'urgent':
        return <UrgentIcon fontSize="small" sx={{ color: TICKET_STATUSES.urgent.color }} />;
      case 'closed':
        return <ResolvedIcon fontSize="small" sx={{ color: TICKET_STATUSES.closed.color }} />;
      default:
        return <OpenIcon fontSize="small" />;
    }
  };

  // Category lookup function
  const getCategoryDetails = (categoryId) => {
    return TICKET_CATEGORIES.find(cat => cat.id === categoryId) || { 
      label: 'Unknown', 
      color: '#999', 
      icon: null 
    };
  };

  // Recent activity - newest 5 tickets
  const recentActivity = [...tickets]
    .sort((a, b) => new Date(b.lastActivity) - new Date(a.lastActivity))
    .slice(0, 5);

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'rgba(30, 40, 55, 0.95)',
            p: 1.5,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 1,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Typography variant="body2" sx={{ color: 'white', mb: 0.5 }}>
            {payload[0].name}
          </Typography>
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 'medium' }}>
            {`${payload[0].value} tickets (${Math.round((payload[0].value / stats.total) * 100)}%)`}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  // Custom pie chart label
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index, name }) => {
    const RADIAN = Math.PI / 180;
    const radius = outerRadius * 1.1;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    if (percent < 0.05) return null; // Don't show labels for small segments

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize="12"
      >
        {`${name}: ${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Box sx={{ py: 2 }}>
      {/* Header */}
      <Typography variant="h5" sx={{ mb: 3, color: 'white', fontWeight: 'bold' }}>
        Tickets Dashboard
      </Typography>

      {/* Stats cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: 'rgba(30, 40, 55, 0.8)', boxShadow: 1, color: 'white' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="overline" display="block" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Total Tickets
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: 'rgba(30, 40, 55, 0.8)', boxShadow: 1, color: 'white', borderLeft: `4px solid ${TICKET_STATUSES.open.color}` }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="overline" display="block" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Open
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {stats.open}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: 'rgba(30, 40, 55, 0.8)', boxShadow: 1, color: 'white', borderLeft: `4px solid ${TICKET_STATUSES.pending.color}` }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="overline" display="block" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Pending
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: 'rgba(30, 40, 55, 0.8)', boxShadow: 1, color: 'white', borderLeft: `4px solid ${TICKET_STATUSES.urgent.color}` }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="overline" display="block" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Urgent
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {stats.urgent}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: 'rgba(30, 40, 55, 0.8)', boxShadow: 1, color: 'white', borderLeft: `4px solid ${TICKET_STATUSES.closed.color}` }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="overline" display="block" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Closed
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {stats.closed}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Card sx={{ bgcolor: 'rgba(30, 40, 55, 0.8)', boxShadow: 1, color: 'white', borderLeft: '4px solid #9c27b0' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="overline" display="block" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Unassigned
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                {stats.unassigned}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Category distribution - Now using recharts PieChart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, bgcolor: 'rgba(30, 40, 55, 0.8)', boxShadow: 1, color: 'white', height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <FilterIcon sx={{ mr: 1 }} /> Ticket Categories
            </Typography>
            <Box sx={{ height: 300 }}>
              {categoryData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomizedLabel}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {categoryData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.color || CHART_COLORS[index % CHART_COLORS.length]} 
                        />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                    <Legend
                      layout="vertical"
                      verticalAlign="middle"
                      align="right"
                      payload={
                        categoryData.map((entry, index) => ({
                          id: entry.name,
                          type: 'square',
                          value: `${entry.name} (${entry.value})`,
                          color: entry.color || CHART_COLORS[index % CHART_COLORS.length],
                        }))
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No category data available</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Status trends - Now using recharts BarChart */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, bgcolor: 'rgba(30, 40, 55, 0.8)', boxShadow: 1, color: 'white', height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <TrendingUpIcon sx={{ mr: 1 }} /> Ticket Status Distribution
            </Typography>
            <Box sx={{ height: 300 }}>
              {statusData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={statusData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fill: 'rgba(255,255,255,0.7)' }} 
                    />
                    <YAxis 
                      tick={{ fill: 'rgba(255,255,255,0.7)' }} 
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" name="Tickets">
                      {statusData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.color} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No status data available</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Recent activity */}
        <Grid item xs={12} lg={4}>
          <Paper sx={{ p: 3, bgcolor: 'rgba(30, 40, 55, 0.8)', boxShadow: 1, color: 'white', height: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <ScheduleIcon sx={{ mr: 1 }} /> Recent Activity
            </Typography>
            
            {recentActivity.length > 0 ? (
              <Box sx={{ mt: 2 }}>
                {recentActivity.map((ticket) => (
                  <Box 
                    key={ticket.id} 
                    sx={{ 
                      mb: 2, 
                      pb: 2, 
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                      '&:last-child': { borderBottom: 'none', pb: 0 }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <StatusIcon status={ticket.status} />
                      <Typography 
                        variant="subtitle2" 
                        sx={{ ml: 1, flex: 1, cursor: 'pointer' }}
                        onClick={() => handleRowClick(ticket.id)}
                      >
                        {ticket.subject}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
                        {ticket.id} • {formatTimestamp(ticket.lastActivity)}
                      </Typography>
                      <Chip 
                        label={getCategoryDetails(ticket.category).label}
                        size="small"
                        sx={{ 
                          backgroundColor: `${getCategoryDetails(ticket.category).color}30`,
                          color: getCategoryDetails(ticket.category).color,
                          height: 22,
                          fontSize: '0.7rem'
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 2, textAlign: 'center' }}>
                No recent activity to display
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Ticket summary */}
        <Grid item xs={12} lg={8}>
          <Paper sx={{ p: 3, bgcolor: 'rgba(30, 40, 55, 0.8)', boxShadow: 1, color: 'white' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                Ticket History
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  placeholder="Search tickets..."
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={handleSearch}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                      </InputAdornment>
                    ),
                    sx: {
                      color: 'white',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.5)'
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)'
                      }
                    }
                  }}
                />

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="status-filter-label" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Status</InputLabel>
                  <Select
                    labelId="status-filter-label"
                    id="status-filter"
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    input={<OutlinedInput label="Status" />}
                    sx={{
                      color: 'white',
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.5)'
                      }
                    }}
                  >
                    <MenuItem value="all">All Statuses</MenuItem>
                    {Object.entries(TICKET_STATUSES).map(([key, { label }]) => (
                      <MenuItem key={key} value={key}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel id="category-filter-label" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Category</InputLabel>
                  <Select
                    labelId="category-filter-label"
                    id="category-filter"
                    value={categoryFilter}
                    onChange={handleCategoryFilterChange}
                    input={<OutlinedInput label="Category" />}
                    sx={{
                      color: 'white',
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.3)'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.5)'
                      }
                    }}
                  >
                    <MenuItem value="all">All Categories</MenuItem>
                    {TICKET_CATEGORIES.map(category => (
                      <MenuItem key={category.id} value={category.id}>{category.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>ID</TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>Subject</TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>Customer</TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>Status</TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>Category</TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>Last Updated</TableCell>
                    <TableCell sx={{ color: 'rgba(255, 255, 255, 0.7)', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredTickets.length > 0 ? (
                    (rowsPerPage > 0
                      ? filteredTickets.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      : filteredTickets
                    ).map((ticket) => (
                      <TableRow 
                        key={ticket.id}
                        hover
                        onClick={() => handleRowClick(ticket.id)}
                        sx={{ 
                          cursor: 'pointer',
                          backgroundColor: ticket.unread ? 'rgba(76, 133, 240, 0.1)' : 'transparent',
                          '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }
                        }}
                      >
                        <TableCell sx={{ color: 'white', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>
                          {ticket.id}
                        </TableCell>
                        <TableCell sx={{ color: 'white', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {ticket.unread && (
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: '50%',
                                  backgroundColor: '#4C85F0',
                                  mr: 1
                                }}
                              />
                            )}
                            {ticket.subject}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'white', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar 
                              src={ticket.customer.avatar} 
                              alt={ticket.customer.name} 
                              sx={{ width: 24, height: 24, mr: 1 }}
                            >
                              {ticket.customer.name.charAt(0)}
                            </Avatar>
                            {ticket.customer.name}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'white', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>
                          <Chip 
                            label={TICKET_STATUSES[ticket.status]?.label || ticket.status}
                            size="small"
                            sx={{ 
                              backgroundColor: `${TICKET_STATUSES[ticket.status]?.color || '#999'}30`,
                              color: TICKET_STATUSES[ticket.status]?.color || '#999',
                              height: 24
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: 'white', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box 
                              component="span" 
                              sx={{ 
                                mr: 1,
                                '& svg': { 
                                  color: getCategoryDetails(ticket.category).color,
                                  fontSize: '1rem'
                                }
                              }}
                            >
                              {getCategoryDetails(ticket.category).icon}
                            </Box>
                            {getCategoryDetails(ticket.category).label}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: 'white', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>
                          {formatTimestamp(ticket.lastActivity)}
                        </TableCell>
                        <TableCell sx={{ color: 'white', borderBottomColor: 'rgba(255, 255, 255, 0.1)' }}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMenuOpen(e, ticket.id);
                            }}
                            sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                          >
                            <MoreIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell 
                        colSpan={7} 
                        sx={{ 
                          textAlign: 'center',
                          color: 'rgba(255, 255, 255, 0.7)',
                          borderBottomColor: 'rgba(255, 255, 255, 0.1)',
                          py: 3
                        }}
                      >
                        No tickets found matching your filters
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
              component="div"
              count={filteredTickets.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)',
                '.MuiTablePagination-selectIcon': { color: 'rgba(255, 255, 255, 0.7)' },
                '.MuiTablePagination-displayedRows': { color: 'rgba(255, 255, 255, 0.7)' },
                '.MuiTablePagination-selectLabel': { color: 'rgba(255, 255, 255, 0.7)' },
                '.MuiTablePagination-actions': {
                  '& .MuiIconButton-root': {
                    color: 'rgba(255, 255, 255, 0.7)'
                  },
                  '& .Mui-disabled': {
                    color: 'rgba(255, 255, 255, 0.3)'
                  }
                }
              }}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(30, 40, 55, 0.95)',
            backdropFilter: 'blur(10px)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
          }
        }}
      >
        <MenuItem onClick={() => {
          onSelectTicket(menuTicketId);
          handleMenuClose();
        }}>
          View Details
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>Mark as Read</MenuItem>
        <MenuItem onClick={handleMenuClose}>Assign</MenuItem>
        <MenuItem onClick={handleMenuClose}>Change Status</MenuItem>
      </Menu>
    </Box>
  );
};

export default DashboardTab;
